/**
 * Money-flow Sankey on static Candidate and Committee pages (sites#178).
 *
 * The pages are static HTML from the rundeck page builders; nginx injects
 * this script on /candidates/ and /committees/ detail pages. Each money card
 * (candidate Top Contributors and Independent Expenditures Supporting and
 * Opposing; committee Funded By, Committees Funded, Candidates Funded and
 * Independent Expenditures For and Against) gets a Sankey | Table toggle, with the Sankey of that
 * card's rows as the default view. A card with no rows in the API response
 * (e.g. IE before the graph has IE_SUPPORTS) keeps its table. The chart data comes from `moneyFlow` on
 * api-graphql. The table stays in the page: on any failure there is no
 * toggle and the page is unchanged.
 *
 * It also registers window.electinfoMoneyFlow (flow-types.ts): the vendor
 * detail page (sites#261) loads this bundle on demand and mounts a card on its
 * Top Committee Clients table.
 */

import { canonicalPath } from '@electinfo/common';
import { currentCycle } from './cycle-facet';
import { escapeHtml, partyColor, readFecId, usd } from './entity-page';
import * as echarts from 'echarts/core';
import { SankeyChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { FLOW_LAYOUT, flowChartHeight, type FlowCard, type FlowLink, type FlowNode } from './flow-types';

echarts.use([SankeyChart, TooltipComponent, CanvasRenderer]);

interface MoneyFlow {
  fecId: string;
  kind: 'Committee' | 'Candidate';
  name: string | null;
  nodes: FlowNode[];
  links: FlowLink[];
}

/** One money card on the page and the slice of the flow it shows. */
interface CardSpec {
  sectionId: string;
  /** Heading the toggle sits beside. */
  heading: (section: HTMLElement) => HTMLElement | null;
  /** The static list or table the Sankey stands in for. */
  list: (section: HTMLElement) => HTMLElement | null;
  side: 'in' | 'out';
  /** Counterparty nodes on this card (API node ids are in:/out: prefixed). */
  pick: (n: FlowNode) => boolean;
}

const QUERY = `query MoneyFlow($fecId: String!) {
  moneyFlow(fecId: $fecId) {
    fecId kind name
    nodes { id side kind entityId fecId name party affinityDem affinityRep amount count otherCount }
    links { source target amount count flow }
  }
}`;

// With a cycle selected (the cycle facet, sites#194): totals and Other are that
// cycle's, as api-graphql filters edges before aggregating. A separate fixed
// document, so the all-cycles request stays exactly as before.
const CYCLE_QUERY = `query MoneyFlowCycle($fecId: String!, $cycle: Int!) {
  moneyFlow(fecId: $fecId, cycle: $cycle) {
    fecId kind name
    nodes { id side kind entityId fecId name party affinityDem affinityRep amount count otherCount }
    links { source target amount count flow }
  }
}`;

// Conduits pass through earmarked individual contributions; flag them so a
// dominant ActBlue/WinRed inflow is not read as the conduit's own money.
const CONDUITS: Record<string, string> = {
  C00401224: 'ActBlue',
  C00694323: 'WinRed',
};

const NEUTRAL = '#94a3b8';
const FOCAL = '#334155';
const OTHER = '#cbd5e1';

const VIEW_KEY = 'electinfo:money-flow-view';
type View = 'sankey' | 'table';

const listUnder = (s: HTMLElement) => s.querySelector<HTMLElement>('ul.candidate-list');
const h2Of = (s: HTMLElement) => s.querySelector<HTMLElement>('h2');

const COMMITTEE_CARDS: CardSpec[] = [
  {
    sectionId: 'incoming', // Funded By
    heading: h2Of,
    list: listUnder,
    side: 'in',
    pick: (n) => n.side === 'in',
  },
  {
    sectionId: 'outgoing', // Committees Funded
    heading: h2Of,
    list: listUnder,
    side: 'out',
    pick: (n) => n.side === 'out' && (n.kind === 'Committee' || n.id === 'out:other-committees'),
  },
  {
    sectionId: 'funded', // Candidates Funded
    heading: h2Of,
    list: listUnder,
    side: 'out',
    pick: (n) => n.side === 'out' && (n.kind === 'Candidate' || n.id === 'out:other-candidates'),
  },
  {
    sectionId: 'ie-support', // Independent Expenditures For
    heading: h2Of,
    list: listUnder,
    side: 'out',
    pick: (n) => n.side === 'ie-out',
  },
  {
    sectionId: 'ie-oppose', // Independent Expenditures Against
    heading: h2Of,
    list: listUnder,
    side: 'out',
    pick: (n) => n.side === 'ie-opp-out',
  },
];

const CANDIDATE_CARDS: CardSpec[] = [
  {
    sectionId: 'funding', // Top Contributors
    heading: (s) =>
      Array.from(s.querySelectorAll<HTMLElement>('h3')).find((h) => /top contributors/i.test(h.textContent || '')) ??
      null,
    list: (s) => s.querySelector<HTMLElement>('table'),
    side: 'in',
    pick: (n) => n.side === 'in',
  },
  {
    sectionId: 'ie-support', // Independent Expenditures Supporting
    heading: h2Of,
    list: (s) => s.querySelector<HTMLElement>('table'),
    side: 'in',
    pick: (n) => n.side === 'ie-in',
  },
  {
    sectionId: 'ie-oppose', // Independent Expenditures Opposing
    heading: h2Of,
    list: (s) => s.querySelector<HTMLElement>('table'),
    side: 'in',
    pick: (n) => n.side === 'ie-opp-in',
  },
];

/** Party code first; committees without one fall back to party affinity. */
export function nodeColor(n: FlowNode): string {
  if (n.side === 'focal') return FOCAL;
  if (n.kind === 'Other') return OTHER;
  if (n.kind === 'Purpose') return '#0d9488';
  if (n.kind === 'Group') return NEUTRAL;
  return partyColor(n.party, n.affinityDem, n.affinityRep) ?? NEUTRAL;
}

function nodeUrl(n: FlowNode): string | null {
  if (!n.entityId || n.side === 'focal' || n.kind === 'Other' || n.kind === 'Vendor' || n.kind === 'Purpose' || n.kind === 'Group') return null;
  return `/${canonicalPath(n.kind, n.entityId)}/`;
}

async function fetchFlow(fecId: string, cycle: number | null): Promise<MoneyFlow | null> {
  const res = await fetch('/api/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cycle ? { query: CYCLE_QUERY, variables: { fecId, cycle } } : { query: QUERY, variables: { fecId } }),
  });
  if (!res.ok) return null;
  const body = await res.json();
  return (body?.data?.moneyFlow as MoneyFlow) ?? null;
}

/** The focal node plus one card's counterparties and the links between them. */
function sliceFlow(flow: MoneyFlow, card: CardSpec): { nodes: FlowNode[]; links: FlowLink[] } {
  const picked = new Set(flow.nodes.filter(card.pick).map((n) => n.id));
  const links = flow.links.filter((l) => picked.has(card.side === 'in' ? l.source : l.target));
  const used = new Set(links.map((l) => (card.side === 'in' ? l.source : l.target)));
  const nodes = flow.nodes.filter((n) => n.side === 'focal' || used.has(n.id));
  return { nodes, links };
}

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'sankey';
  } catch {
    return 'sankey';
  }
}

function saveView(view: View): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Storage blocked: the toggle still works for this page view.
  }
}

const SANKEY_ICON =
  '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6">' +
  '<path d="M2 3v4M2 9v4M14 2v12"/><path d="M2 5c6 0 6-1.5 12-1.5M2 11c6 0 6 1.5 12 1.5" opacity=".7"/></svg>';
const TABLE_ICON =
  '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6">' +
  '<rect x="2" y="2.5" width="12" height="11" rx="1"/><path d="M2 6.5h12M2 10h12M6.5 6.5v7"/></svg>';

function injectStyles(doc: Document): void {
  if (doc.getElementById('money-flow-style')) return;
  const style = doc.createElement('style');
  style.id = 'money-flow-style';
  style.textContent = `
.mf-head{display:flex;align-items:center;gap:.75rem}
.mf-head>h2,.mf-head>h3{flex:1 1 auto}
.mf-toggle{display:inline-flex;flex:none;border:1px solid #cbd5e1;border-radius:6px;overflow:hidden}
.mf-view-btn{display:inline-flex;align-items:center;justify-content:center;background:#fff;border:0;margin:0;padding:4px 8px;cursor:pointer;color:#475569}
.mf-view-btn+.mf-view-btn{border-left:1px solid #cbd5e1}
.mf-view-btn[aria-pressed="true"]{background:#1e293b;color:#fff}
.mf-view-btn:focus-visible{outline:2px solid #2563eb;outline-offset:-2px}
.money-flow-chart{width:100%;margin:.5rem 0 1rem}
.mf-note{margin:-.5rem 0 1rem}`;
  doc.head.appendChild(style);
}

const { top: TOP, bottom: BOTTOM, nodeGap: NODE_GAP, minLabelPx: MIN_LABEL_PX } = FLOW_LAYOUT;

/**
 * Small flows get thin nodes whose labels would stack on each other (ECharts
 * does not de-overlap Sankey labels). Both columns of a card's Sankey carry
 * the same total, so a node's drawn height is about its share of the column
 * after the gaps; label only nodes at least MIN_LABEL_PX tall. The tooltip
 * still names the rest.
 */
function labelFilter(links: FlowLink[], side: 'in' | 'out', plotHeight: number): (n: FlowNode) => boolean {
  const value = new Map<string, number>();
  let total = 0;
  for (const l of links) {
    const id = side === 'in' ? l.source : l.target;
    value.set(id, (value.get(id) ?? 0) + (l.display ?? l.amount));
    total += l.display ?? l.amount;
  }
  const scale = total > 0 ? (plotHeight - NODE_GAP * Math.max(0, links.length - 1)) / total : 0;
  return (n) => n.side === 'focal' || (value.get(n.id) ?? 0) * scale >= MIN_LABEL_PX;
}

function drawChart(el: HTMLElement, nodes: FlowNode[], links: FlowLink[], side: 'in' | 'out'): echarts.ECharts {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const height = el.clientHeight || 260;
  const threeColumns = nodes.some(n => n.side === 'mid');
  // Label heights are relative to each column, not the doubled two-stage link total.
  const showLabel = threeColumns ? (n: FlowNode) => {
    if (n.side === 'focal') return true;
    const column = nodes.filter(other => other.side === n.side);
    const size = (x: FlowNode) => Math.max(0, x.display ?? x.amount ?? 0);
    const total = column.reduce((sum, other) => sum + size(other), 0);
    return total > 0 && size(n) / total * (height - TOP - BOTTOM - NODE_GAP * (column.length - 1)) >= MIN_LABEL_PX;
  } : labelFilter(links, side, height - TOP - BOTTOM);
  // Counterparty labels face the focal node; the focal label faces outward.
  const position = (n: FlowNode) => {
    const left = n.side === 'focal' ? side === 'out' : side === 'in';
    return left ? 'right' : 'left';
  };

  const chart = echarts.init(el, undefined, { renderer: 'canvas' });
  chart.setOption({
    // Purpose cards must reveal all three columns immediately; legacy cards keep their animation.
    ...(threeColumns ? { animation: false } : {}),
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (p: any) => {
        if (p.dataType === 'edge') {
          const s = byId.get(p.data.source);
          const t = byId.get(p.data.target);
          const l = p.data.raw as FlowLink;
          const what =
            l.flow === 'transfer'
              ? 'transfers'
              : l.flow === 'ie'
                ? 'expenditures'
                : l.flow === 'ie-oppose'
                  ? 'expenditures against'
                  : l.flow === 'payment'
                    ? 'payments'
                    : 'contributions';
          const count = l.count != null ? ` (${l.count.toLocaleString('en-US')} ${what})` : '';
          return `${escapeHtml(s?.name ?? '')} → ${escapeHtml(t?.name ?? '')}<br><b>${usd(l.amount)}</b>${count}`;
        }
        const n = byId.get(p.name);
        if (!n) return '';
        const conduit = n.fecId && CONDUITS[n.fecId] ? '<br><i>Conduit: passes through earmarked individual contributions</i>' : '';
        const id = n.fecId ? ` <span style="color:#64748b">${n.fecId}</span>` : '';
        const amount = n.side !== 'focal' && n.amount != null ? `<br><b>${usd(n.amount)}</b>` : '';
        return `${escapeHtml(n.name)}${id}${amount}${conduit}`;
      },
    },
    series: [
      {
        type: 'sankey',
        left: 8,
        right: 8,
        top: TOP,
        bottom: BOTTOM,
        nodeWidth: 12,
        nodeGap: NODE_GAP,
        nodeAlign: 'justify',
        // Keep the API's order (amount desc, as in the table) instead of relaxing it.
        layoutIterations: 0,
        draggable: false,
        emphasis: { focus: 'adjacency' },
        data: nodes.map((n) => ({
          name: n.id,
          raw: n,
          // Counterparties left of the focal node (in) or right of it (out), categories between.
          ...(threeColumns ? { depth: n.side === 'mid' ? 1 : (n.side === 'focal') === (side === 'in') ? 2 : 0 } : {}),
          itemStyle: { color: nodeColor(n), borderColor: nodeColor(n) },
          label: {
            show: showLabel(n),
            position: position(n),
            formatter: () => (n.name.length > 42 ? `${n.name.slice(0, 40)}…` : n.name),
            fontSize: 11,
            color: '#1e293b',
          },
          cursor: nodeUrl(n) ? 'pointer' : 'default',
        })),
        links: links.map((l) => ({
          source: l.source,
          target: l.target,
          value: l.display ?? l.amount,
          raw: l,
          // Colour each link by its counterparty, not the focal node; widened (not to scale) links fainter.
          lineStyle: { color: side === 'in' ? 'source' : 'target', opacity: (l.display ?? l.amount) > l.amount ? 0.15 : 0.35 },
        })),
        label: { show: true },
        lineStyle: { curveness: 0.5 },
      },
    ],
  });

  chart.on('click', (p: any) => {
    if (p.dataType !== 'node') return;
    const n = byId.get(p.name);
    const url = n && nodeUrl(n);
    if (url) window.location.href = url;
  });
  return chart;
}

const charts = new Set<echarts.ECharts>();
const cleanups = new WeakMap<HTMLElement, () => void>();
const mountedCharts = new WeakMap<HTMLElement, () => echarts.ECharts | null>();

/** One page card from the moneyFlow response (Candidate and Committee pages). */
function mountCard(doc: Document, flow: MoneyFlow, card: CardSpec, initial: View): boolean {
  const section = doc.getElementById(card.sectionId);
  if (!section) return false;
  const heading = card.heading(section);
  const list = card.list(section);
  if (!heading || !list) return false;
  return mountFlowCard(doc, { section, heading, list, side: card.side, ...sliceFlow(flow, card) }, initial);
}

/**
 * Turn one section into a Sankey | Table card. The chart is created on first
 * show (ECharts cannot measure a hidden element) and the list is hidden, not
 * removed, so Table view is the original markup.
 */
function mountFlowCard(doc: Document, card: FlowCard, initial: View): boolean {
  const { section, heading, list, nodes, links } = card;
  if (section.dataset.moneyFlow || !links.length) return false;
  injectStyles(doc);
  section.dataset.moneyFlow = '1';

  const chartEl = doc.createElement('div');
  chartEl.className = 'money-flow-chart';
  const rowCount = nodes.some(n => n.side === 'mid')
    ? Math.max(...(['in', 'mid', 'focal', 'out'] as const).map(side => nodes.filter(n => n.side === side).length))
    : links.length;
  chartEl.style.height = `${flowChartHeight(rowCount)}px`;
  chartEl.setAttribute('role', 'img');
  chartEl.setAttribute('aria-label', `${heading.textContent?.trim() || 'Money flows'} as a Sankey diagram. Switch to table view for the list.`);
  list.parentNode!.insertBefore(chartEl, list);
  let noteEl: HTMLElement | null = null;
  if (card.note) {
    noteEl = doc.createElement('p');
    noteEl.className = 'meta mf-note';
    noteEl.textContent = card.note;
    list.parentNode!.insertBefore(noteEl, list);
  }

  const head = doc.createElement('div');
  head.className = 'mf-head';
  heading.parentNode!.insertBefore(head, heading);
  head.appendChild(heading);
  const toggle = doc.createElement('div');
  toggle.className = 'mf-toggle';
  toggle.setAttribute('role', 'group');
  toggle.setAttribute('aria-label', 'View');
  toggle.innerHTML =
    `<button type="button" class="mf-view-btn" data-view="sankey" title="Sankey view" aria-label="Sankey view">${SANKEY_ICON}</button>` +
    `<button type="button" class="mf-view-btn" data-view="table" title="Table view" aria-label="Table view">${TABLE_ICON}</button>`;
  head.appendChild(toggle);

  let chart: echarts.ECharts | null = null;
  mountedCharts.set(section, () => chart);
  // Stylesheet loading and responsive card changes need not fire window.resize.
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
    if (chartEl.clientWidth > 0) chart?.resize();
  });
  observer?.observe(chartEl);
  const show = (view: View) => {
    const sankey = view === 'sankey';
    chartEl.style.display = sankey ? '' : 'none';
    if (noteEl) noteEl.style.display = sankey ? '' : 'none';
    list.style.display = sankey ? 'none' : '';
    toggle.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
    if (sankey && !chart) {
      chart = drawChart(chartEl, nodes, links, card.side);
      charts.add(chart);
    }
    if (sankey) {
      chart?.resize();
      requestAnimationFrame(() => { if (chartEl.clientWidth > 0) chart?.resize(); });
    }
  };
  toggle.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-view]');
    if (!btn) return;
    const view = btn.dataset.view as View;
    show(view);
    saveView(view);
  });

  const cleanup = () => {
    observer?.disconnect();
    mountedCharts.delete(section);
    if (chart) { charts.delete(chart); chart.dispose(); chart = null; }
    chartEl.remove();
    noteEl?.remove();
    head.replaceWith(heading);
    list.style.display = '';
    delete section.dataset.moneyFlow;
    cleanups.delete(section);
  };
  cleanups.set(section, cleanup);
  try {
    show(initial);
  } catch (err) {
    // Chart failed: fall back to the static list and drop the toggle.
    cleanup();
    throw err;
  }
  return true;
}

window.electinfoMoneyFlow = {
  inspect(section: HTMLElement) {
    const chart = mountedCharts.get(section)?.();
    return chart ? { width: chart.getWidth(), option: chart.getOption() } : null;
  },
  unmount(section: HTMLElement): void { cleanups.get(section)?.(); },
  mount(card: FlowCard): boolean {
    const mounted = mountFlowCard(document, card, readView());
    if (mounted) watchResize();
    return mounted;
  },
};

let resizeWatched = false;

function watchResize(): void {
  if (resizeWatched) return;
  resizeWatched = true;
  window.addEventListener('resize', () => charts.forEach((c) => c.resize()));
}

async function init(): Promise<void> {
  const w = window as unknown as { __electinfoMoneyFlow?: boolean };
  if (w.__electinfoMoneyFlow) return; // nginx injection + builder tag
  w.__electinfoMoneyFlow = true;

  const fecId = readFecId(document);
  if (!fecId) return;
  try {
    const flow = await fetchFlow(fecId, currentCycle());
    if (!flow || !flow.links?.length) return;
    const initial = readView();
    for (const card of flow.kind === 'Committee' ? COMMITTEE_CARDS : CANDIDATE_CARDS) {
      try {
        mountCard(document, flow, card, initial);
      } catch (err) {
        console.warn(`money-flow: #${card.sectionId} not rendered`, err);
      }
    }
    watchResize();
  } catch (err) {
    console.warn('money-flow: not rendered', err);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void init());
} else {
  void init();
}
